import React, { useState, useEffect, useRef, useMemo } from 'react';
import HeaderWithIcon from '../../components/common/HeaderWithIcon';
import Filters from '../../components/common/Filters';
import Calendar from '../../components/common/Calendar';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Percent,
  TrendingUp,
  Trophy,
  Wallet,
} from 'lucide-react';
import Button from '../../components/ui/Button';
import ExpandableTable from '../../components/ExpandableTable';
import DrawsExpandedContent from './DrawsExpandedContent';
import { reportsApi } from '../../api/reports';
import { useAuth } from '../../context/AuthContext';
import { gamesApi } from '../../api/games';
import { toCurrency, userRoleName } from '../../utils/helper';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

const rolesDropdown = [
  {
    id: 1,
    name: 'Admin',
  },
  {
    id: 2,
    name: 'Incorporator',
  },
  {
    id: 3,
    name: 'Sub Operator',
  },
  {
    id: 4,
    name: 'General Coordinator',
  },
  {
    id: 5,
    name: 'Coordinator',
  },
  {
    id: 6,
    name: 'Sub Coordinator',
  },
  {
    id: 7,
    name: 'Agent',
  },
  {
    id: 8,
    name: 'Sub Agent',
  },
  {
    id: 9,
    name: 'Player',
  },
];

const DrawTimeDropdown = React.memo(
  ({ selectedDrawTime, drawTimeOpen, setDrawTimeOpen }) => {
    return (
      <button
        id='dropdownButton'
        className='w-full p-2.5 text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center'
        onClick={() => setDrawTimeOpen(!drawTimeOpen)}
      >
        <span>{selectedDrawTime || 'All'}</span>
        <span className='text-gray-500'>
          {drawTimeOpen ? (
            <ArrowUp className='h-4 w-4' />
          ) : (
            <ArrowDown className='h-4 w-4' />
          )}
        </span>
      </button>
    );
  }
);

export default function UserReport() {
  const { user, tokens } = useAuth();
  const [allReports, setAllReports] = useState([]); // Store all fetched reports
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawTimeOpen, setDrawTimeOpen] = useState(false);
  const [selectedDrawTime, setSelectedDrawTime] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedUserType, setSelectedUserType] = useState(() => {
    const defaultRole = rolesDropdown.find(
      (role) => role.id === user.role_id + 1
    );
    return defaultRole || null;
  });
  const [gameSettings, setGameSettings] = useState([]);
  const dropdownRef = useRef(null); // Add this ref for the dropdown

  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [gameId, setGameId] = useState('');

  useEffect(() => {
    fetchReports();
  }, [tokens.access.token]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await reportsApi.userReport(
        tokens.access.token,
        1,
        20,
        selectedStartDate?.toISOString(),
        selectedEndDate?.toISOString()
      );

      if (response.status === 200) {
        setAllReports(response.data);
      } else {
        setError('Failed to fetch reports');
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  // fetch game settings
  useEffect(() => {
    const fetchGameSettings = async () => {
      const response = await gamesApi.getAllGames(tokens.access.token);
      if (response.status === 200) {
        setGameSettings(response.data);
      } else {
        setError('Failed to fetch game settings');
      }
    };
    fetchGameSettings();
  }, [tokens.access.token]);

  const reports = useMemo(() => {
    let arrayOfObjects = Object.values(allReports?.userReports || {});

    // Sort the array based on the 'name' property
    arrayOfObjects.sort((a, b) => a.name.trim().localeCompare(b.name.trim()));

    if (selectedUserType) {
      arrayOfObjects = arrayOfObjects.filter(
        (report) => report.role_id === selectedUserType.id
      );
    }

    if (selectedUser) {
      arrayOfObjects = arrayOfObjects.filter(
        (report) =>
          report.user_parent.includes(selectedUser.user_id) &&
          report.user_id !== selectedUser.user_id
      );
    }

    return arrayOfObjects;
  }, [allReports, selectedUserType, selectedUser]);

  // get reports of the user
  const ownReports = useMemo(() => {
    let arrayOfObjects = Object.values(allReports?.userReports || {});
    arrayOfObjects = arrayOfObjects.filter(
      (report) => report.role_id === user.role_id
    );
    return arrayOfObjects;
  }, [allReports, user]);

  const overallReports = useMemo(() => {
    // Use reduce instead of forEach for better performance
    const totals = reports.reduce(
      (acc, report) => {
        return {
          gross: acc.gross + (report.total || 0),
          hits: acc.hits + (report.hits || 0),
          commissions: acc.commissions + (report.commissions || 0),
        };
      },
      { gross: 0, hits: 0, commissions: 0 }
    );

    return [
      {
        title: 'Gross Sales',
        icon: <TrendingUp className='w-4' />,
        iconBgColor: 'bg-blue-600',
        key: 'gross',
        amount: totals?.gross,
      },
      {
        title: 'Commissions',
        icon: <Percent className='w-4' />,
        iconBgColor: 'bg-purple-600',
        key: 'commissions',
        amount: totals?.commissions,
      },
      {
        title: 'Payout',
        icon: <Trophy className='w-4' />,
        key: 'hits',
        iconBgColor: 'bg-red-600',
        amount: totals?.hits,
      },
      {
        title: 'Net Sales',
        icon: <Wallet className='w-4' />,
        key: 'net',
        iconBgColor: 'bg-yellow-300',
        amount: totals?.gross - totals?.hits - totals?.commissions,
      },
    ];
  }, [reports]);

  const drawTimes = useMemo(() => {
    if (gameId) {
      return (
        gameSettings?.find((game) => game?.id === gameId)?.otherSettings
          ?.drawTime || []
      );
    }
    return [];
  }, [gameSettings, gameId]);

  const gameObject = useMemo(() => {
    if (!gameSettings) return {};
    const gameObj = {};
    gameSettings.forEach((game) => {
      gameObj[game.id] = game;
    });
    return gameObj;
  }, [gameSettings]);

  const headers = useMemo(() => {
    return [
      {
        key: 'name',
        label: 'Name',
        width: '220px',
        minWidth: '180px',
        type: 'custom',
        customCell: ({ item }) => {
          return (
            <div className='flex flex-col items-start'>
              <div className='text-sm font-medium text-black'>{item.name}</div>
              <div className='text-xs text-gray-500'>
                {userRoleName(item.role_id)}
              </div>
            </div>
          );
        },
      },
      { key: 'total', label: 'Gross Sales', type: 'currency' },
      { key: 'commissions', label: 'Commissions', type: 'currency' },
      { key: 'hits', label: 'Payout', type: 'currency', width: '130px' },
      {
        key: 'kabi_tap',
        label: 'Net Sales',
        type: 'custom',
        width: '150px',
        customCell: ({ item }) => {
          return (
            <div className='text-sm font-medium text-black flex items-center gap-1'>
              {item.total - item.commissions - item.hits > 0 ? (
                <ArrowUp className='w-4 text-green-500' />
              ) : (
                <ArrowDown className='w-4 text-red-500' />
              )}
              ₱{toCurrency(item.total - item.commissions - item.hits)}
            </div>
          );
        },
      },
    ];
  }, [gameObject]);

  const renderExpandableContent = ({ item }) => {
    return (
      <DrawsExpandedContent 
        item={item} 
        gameObject={gameObject}
        allReports={allReports}
        headers={headers}
      />
    );
  };

  return (
    <>
      <div className='min-h-screen  relative overflow-hidden  font-poppins'>
        <HeaderWithIcon title='User Reports' />

        <div className='px-3 sm:px-5 pt-4 relative z-50'>
          <div className='flex flex-row gap-3 sm:gap-4'>
            <div className='space-y-2 flex-1'>
              <Calendar
                label='Start Date'
                key='start-date'
                setSelectedStartDate={setSelectedStartDate}
              ></Calendar>
            </div>
            <div className='space-y-2 flex-1'>
              <Calendar
                label='End Date'
                key='end-date'
                setSelectedEndDate={setSelectedEndDate}
              ></Calendar>
            </div>
          </div>

          <div className='flex flex-row gap-3 sm:gap-4'>
            <div className='space-y-2 flex-1'>
              <Filters
                games={gameSettings}
                label='Select Game'
                onSelectedGame={(game) => {
                  setGameId(game);
                }}
              />
            </div>
            <div className='space-y-2 flex-1'>
              <label htmlFor='game' className='text-xs font-medium '>
                Draw Time
              </label>
              <div className='relative w-full'>
                <DrawTimeDropdown
                  selectedDrawTime={selectedDrawTime}
                  drawTimeOpen={drawTimeOpen}
                  setDrawTimeOpen={setDrawTimeOpen}
                />
                {drawTimeOpen && (
                  <ul className='absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md max-h-48 overflow-y-auto'>
                    <li
                      onClick={() => {
                        setSelectedDrawTime('');
                        setDrawTimeOpen(false);
                      }}
                      className={`p-3 sm:p-2.5 text-sm sm:text-xs text-black hover:bg-[#E1E7EF] cursor-pointer active:bg-[#D1D7DF] transition-colors`}
                    >
                      All
                    </li>
                    {drawTimes.map((time, index) => (
                      <li
                        key={index}
                        className={`p-3 sm:p-2.5 text-sm sm:text-xs text-black hover:bg-[#E1E7EF] cursor-pointer active:bg-[#D1D7DF] transition-colors ${
                          index !== 0 ? 'border-t border-[#E1E7EF]' : ''
                        }`}
                        onClick={() => {
                          setSelectedDrawTime(time);
                          setDrawTimeOpen(false);
                        }}
                      >
                        {dayjs(time, 'HH:mm').format('h:mm A')}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className='flex flex-col flex-row gap-3 sm:gap-4'>
            <div className='space-y-2 sm:w-1/2 '>
              <label htmlFor='game' className='text-xs font-medium '>
                User Type
              </label>
              <div className='relative w-full' ref={dropdownRef}>
                <button
                  id='dropdownButton'
                  className='w-full p-3 sm:p-2.5 text-sm sm:text-xs font-normal text-gray-700 bg-white border rounded-md shadow-sm flex justify-between items-center touch-manipulation'
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span>{selectedUserType?.name || 'All'}</span>
                  <span className='text-gray-500'>
                    {dropdownOpen ? (
                      <ChevronUp className='h-4 w-4' />
                    ) : (
                      <ChevronDown className='h-4 w-4' />
                    )}
                  </span>
                </button>
                {dropdownOpen && (
                  <ul className='absolute left-0 z-10 w-full mt-1 bg-white border rounded-md shadow-md max-h-48 overflow-y-auto'>
                    {rolesDropdown
                      .filter((role) => role.id > user.role_id)
                      .map((role, index) => (
                        <li
                          key={role.id}
                          onClick={() => {
                            setSelectedUserType(role);
                            setDropdownOpen(false);
                          }}
                          className={`p-3 sm:p-2.5 text-sm sm:text-xs text-black hover:bg-[#E1E7EF] cursor-pointer active:bg-[#D1D7DF] transition-colors ${
                            index !== 0 ? 'border-t border-[#E1E7EF]' : ''
                          }`}
                        >
                          {role.name}
                        </li>
                      ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          <div className='flex gap-3 sm:gap-4'>
            <Button
              onClick={() => {
                fetchReports();
              }}
              className='my-3 sm:my-2.5 w-full bg-[#0E64D1] hover:bg-blue-900 active:bg-blue-800 text-white text-base font-medium py-3 sm:py-2 rounded-md flex justify-center touch-manipulation transition-colors'
              text='Filter'
            ></Button>
          </div>
        </div>

        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 px-3 sm:px-5'>
          {overallReports.map((item, index) => (
            <div key={index} className='bg-white rounded-lg shadow p-3 sm:p-[10px] hover:shadow-md transition-shadow'>
              <div className='flex flex-col justify-between h-full'>
                <div className='flex justify-between w-full'>
                  <div>
                    <p className='text-sm leading-none font-medium'>
                      {item.title}
                    </p>
                    <span className='text-[10px] text-gray-500 font-medium'>
                      {item.subtitle}
                    </span>
                  </div>
                  <div
                    className={`flex justify-center items-center rounded-full text-white w-8 h-8 sm:w-7 sm:h-7 ${item.iconBgColor}`}
                  >
                    {item.icon}
                  </div>
                </div>

                {loading ? (
                  <div className='loading-shimmer h-6 rounded-full w-full mt-1'></div>
                ) : (
                  <p className='text-lg sm:text-xl font-semibold text-gray-700 flex items-center gap-1 mt-1'>
                    {item.key === 'net' &&
                      (item.amount > 0 ? (
                        <ArrowUp className='text-green-500 w-4 h-4' />
                      ) : (
                        <ArrowDown className='text-red-500 w-4 h-4' />
                      ))}
                    ₱{toCurrency(item.amount)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className='relative overflow-x-auto rounded-lg mt-4 px-3 sm:px-5 mb-10'>
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-3 text-center sm:text-left">Own Report</h2>
            <ExpandableTable 
              headers={headers} 
              items={ownReports || []} 
              loading={loading}
              expandableContent={renderExpandableContent}
              expandableKey="id"
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold mb-3 text-center sm:text-left">Downline Reports</h2>
            <ExpandableTable 
              headers={headers} 
              items={reports || []} 
              loading={loading}
              expandableContent={renderExpandableContent}
              expandableKey="id"
            />
          </div>
        </div>
      </div>
    </>
  );
}
